/**
 * Constructs a localization data object by parsing either a moniker or BCP-47
 * language tag. The constructed object will be composed of the following
 * properties:
 *      data: Original passed in value.
 *      brand: Brand variant (eg. HA, VRBO).
 *      code: BCP-47 style language tag (ie. no brand variant).
 *      language: The language of the locale (ie. en). ISO 639.
 *      region: The country code (ie. US). ISO 3166-1.
 * @param {string} locale - A moniker or BCP-47 language code.
 */

// NOTE: This code came from https://github.homeawaycorp.com/ui-toolkit/react-date-pickers/blob/master/src/utils/dateUtils.js
// I made a PR to expose it from that repo but UI Toolkit didn't want to support it

function LanguageTag(locale = 'en-US') {
    locale = locale.trim();
    const tags = locale.split(/_|-/);

    this.data = locale;
    this.language = tags[0] && tags[0].length === 2 ? tags[0] : 'en';
    this.region = tags.length > 1 && tags[1] && tags[1].length === 2 ? tags[1] : null;
    this.brand = tags.length > 2 && tags[2] ? tags[2] : null;
    this.code = this.region ? `${this.language}-${this.region}` : this.language;
}

function getLocaleDateString(dateString, locale = 'en_US', options) {
    let languageTag = new LanguageTag(locale);

    if (!dateString) {
        return '';
    }

    const parts = dateString.split('-');
    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
    return dateObj.toLocaleDateString(languageTag.code, options);
}

/* eslint-disable complexity */
function getLocaleConsolidatedDateString(startDateString, endDateString, locale) {
    let result;
    const options = {month: 'short', day: 'numeric'};

    if (!startDateString || !endDateString) {
        result = getLocaleDateString(startDateString || endDateString, locale, options);
    } else {
        const sameMonth = startDateString.split('-')[1] === endDateString.split('-')[1];
        const dates = [
            getLocaleDateString(startDateString, locale, options),
            getLocaleDateString(endDateString, locale, sameMonth ? {day: 'numeric'} : options)
        ];

        if (sameMonth) {
            const breakPoint = getLocaleDateString(startDateString, locale, {day: 'numeric'});
            // Determine if the locale specifies a '.' after the day number.
            // If so, format the date range like "05. - 30. Sep."
            const isDecimalDay = dates[0].indexOf(`${breakPoint}.`) >= 0;
            result = dates[0].replace(
                breakPoint,
                `${breakPoint}${isDecimalDay ? '.' : ''} - ${dates[1]}`
            );
        } else {
            // if days fall in different months, use "mmm dd - mmm dd" or "dd mmm - dd mmm"
            result = dates.join(' - ');
        }
    }
    return result;
}

function getDisplayYearFirst(locale) {
    return locale ? locale.substr(0, 5) === 'ja_JP' || locale.substr(0, 5) === 'ja-JP' : false;
}

export {getLocaleConsolidatedDateString, getLocaleDateString, getDisplayYearFirst, LanguageTag};
